import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  CircularProgress,
  Tooltip,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Table, 
  TableHead,
  TableBody, 
  TableRow, 
  TableCell,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import moment from "moment";
import { FaEdit } from "react-icons/fa";
import { FiTrash2 } from "react-icons/fi";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { fetchStoresRequest } from "../../../redux/StoreList/StoreSlice"; 

import JewelleryDialog from "./AddEditJewellery";
import JewelleryInventory from "./JewelleryInventory";
import ImagePreviewModal from "../Product/ImagePreviewModal";
import ConfirmationDialog from "../../Modals/ConfirmationModal";
import { canShowPermission } from "../Permissions/permissionObject";
import { apiRequest } from "../../../redux/Reports/reportsSlice";
import useCsvConverter from "../../../hooks/convertToCSV";
import {
  getJewelleryRequest,
  saveJewelleryRequest,
  editJewelleryRequest,
  changeJewelleryStatusRequest,
  deleteJewelleryRequest,
  addJewelleryInventoryRequest, 
  toggleJewelleryInventoryRequest,
  editJewelleryInventoryRequest,
} from "../../../redux/Jewellery/JewellerySlice";

const VIDEO_EXT = /\.(mp4|webm|mov|m4v|ogg|avi|mkv)(\?.*)?$/i;
const JEWELLERY_CSV_ENDPOINT = "/api/v1/artificial-jewellery/artificialJewelleryCsv";

const getJewelleryMedia = (row) => {
  const files = Array.isArray(row?.files) ? row.files.filter(Boolean) : [];
  return {
    images: files.filter((f) => !VIDEO_EXT.test(f)),
    videos: files.filter((f) => VIDEO_EXT.test(f)),
  };
};

const formatAmount = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;

  const formatPeriod = (value, unit, zeroLabel) => {
  if (value === null || value === undefined || value === "") return "N.A";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (n === 0) return zeroLabel;
  return `${n} ${unit}${n === 1 ? "" : "s"}`;
};

const formatCategory = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "N.A";

const CellWrap = ({ children, align = "center" }) => (
  <Box
    sx={{
      width: "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent:
        align === "left"
          ? "flex-start"
          : align === "right"
            ? "flex-end"
            : "center",
      minWidth: 0,
      py: 0.5,
    }}
  >
    {children}
  </Box>
);

const Jewellery = () => {
  const [permissions, setPermissions] = useState({});
  useEffect(() => {
    const stored = localStorage.getItem("userPermissions");
    if (stored) {
      try {
        setPermissions(JSON.parse(stored));
      } catch (e) {
        console.error("Failed to parse userPermissions", e);
      }
    }
  }, []);

  const canCreate =
    canShowPermission(permissions, "jewellery", "create") ||
    canShowPermission(permissions, "products", "create");
  const canEdit =
    canShowPermission(permissions, "jewellery", "edit") ||
    canShowPermission(permissions, "products", "edit");
  const canDelete =
    canShowPermission(permissions, "jewellery", "delete") ||
    canShowPermission(permissions, "products", "delete");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0); 
  const [pageSize, setPageSize] = useState(10);
  const [open, setOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [imageOpen, setImageOpen] = useState(false);
  const [previewData, setPreviewData] = useState({
    images: [],
    videos: [],
    productName: "",
  });
  const [statusLoadingId, setStatusLoadingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  const [openDetailsDialog, setOpenDetailsDialog] = useState(false); 
  const [selectedJewellery, setSelectedJewellery] = useState(null); 

  const [inventoryOpen, setInventoryOpen] = useState(false); 
  const [inventoryJewelleryId, setInventoryJewelleryId] = useState(null); 
  const [inventoryResetKey, setInventoryResetKey] = useState(0); 
  

  const lastAction = useRef(null);
  const dispatch = useDispatch();

  const { jewelleryData, total, loading, message } = useSelector(
    (state) => state.jewellery,
    shallowEqual,
  );
  const rows = jewelleryData ?? [];
  const inventoryJewellery =
    rows.find((item) => item._id === inventoryJewelleryId) || null; 
  const stores = useSelector((state) => state.store?.stores, shallowEqual) ?? [];  
  const { convertToCsv } = useCsvConverter();

  // ---------- list fetch ----------
  const fetchList = useCallback(
    (pageIndex = page) => {
      dispatch(
        getJewelleryRequest({
          page: pageIndex + 1,
          limit: pageSize,
          search: debouncedSearch,
        }),
      );
    },
    [dispatch, page, pageSize, debouncedSearch],
  );

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(0);
    }, 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchList();
  }, [page, pageSize, debouncedSearch]);

  useEffect(() => {
    if (!message) return;
    const action = lastAction.current;
    lastAction.current = null;

    if (action === "create" || action === "edit") {
      setOpen(false);
      setEditItem(null);
    }

    if (action === "create") {
      if (page !== 0) setPage(0);
      else fetchList(0);
    }

    if (action === "delete") {
      const nextPage = rows.length === 0 && page > 0 ? page - 1 : page;
      if (nextPage !== page) setPage(nextPage);
      else fetchList(nextPage);
    }

    if (action === "inventoryAdd" || action === "inventoryEdit") { 
      setInventoryResetKey((key) => key + 1);
      fetchList(page);
    }
    if (action === "inventoryStatus") { 
      fetchList(page);
    }
  }, [message]);

    useEffect(() => {
    if (inventoryOpen) {
      dispatch(fetchStoresRequest({ page: 1, limit: 100, search: "" })); 
    }
  }, [inventoryOpen]);

  useEffect(() => {
    if (!loading) setStatusLoadingId(null);
  }, [loading]);

  // ---------- handlers ----------
  const handleViewClick = useCallback((row) => {
    const { images, videos } = getJewelleryMedia(row);
    setPreviewData({ images, videos, productName: row?.name || "" });
    setImageOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setImageOpen(false);
    setPreviewData({ images: [], videos: [], productName: "" });
  }, []);

  const updateStatus = useCallback(
    (id) => {
      if (statusLoadingId) return;
      lastAction.current = "status";
      setStatusLoadingId(id);
      dispatch(changeJewelleryStatusRequest({ id }));
    },
    [dispatch, statusLoadingId],
  );

  const handleSubmit = (formData) => {
    if (editItem?._id) {
      lastAction.current = "edit";
      dispatch(editJewelleryRequest({ id: editItem._id, formData }));
    } else {
      lastAction.current = "create";
      dispatch(saveJewelleryRequest(formData));
    }
  };

  const handleConfirmDelete = () => {
    if (idToDelete) {
      lastAction.current = "delete";
      dispatch(deleteJewelleryRequest(idToDelete));
      setIdToDelete(null);
    }
  };

    const handleAddInventory = (data) => { 
    lastAction.current = "inventoryAdd";
    dispatch(addJewelleryInventoryRequest(data));
  };

  const handleInventoryToggle = (unit) => {
  lastAction.current = "inventoryStatus";
  dispatch(
    toggleJewelleryInventoryRequest({
      id: unit._id,
      isActive: unit.isActive === false, // flips the current status
    })
  );
};

  const handleEditInventory = (data) => {
    lastAction.current = "inventoryEdit";
    dispatch(editJewelleryInventoryRequest(data));
  };

  const triggerCsvDownload = (csvText, filename) => {
    const blob = new Blob([csvText], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadJewelleryCsv = () => {
    dispatch(
      apiRequest({
        method: "GET",
        endpoint: JEWELLERY_CSV_ENDPOINT,
        query: {},
        callback: (response) => {
          const csvRows = response?.data || [];
          triggerCsvDownload(convertToCsv(csvRows), "jewellery.csv");
        },
      }),
    );
  };

  // ---------- columns ----------
  const columns = useMemo(
    () => [
      {
        flex: 0.14,
        field: "productCode",
        minWidth: 130,
        headerName: "Product Code",
        align: "left",
        headerAlign: "left",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="left">
            <Tooltip title={row.productCode ?? ""}>
              <Typography variant="body2" noWrap sx={{ color: "#1e293b", width: "100%" }}>
                {row.productCode ?? "-"}
              </Typography>
            </Tooltip>
          </CellWrap>
        ),
      },
      {
        flex: 0.12,
        minWidth: 110,
        headerName: "Created",
        field: "createdAt",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>
              {moment(row.createdAt).format("DD MMM YYYY")}
            </Typography>
          </CellWrap>
        ),
      },
      {
        flex: 0.1,
        minWidth: 90,
        headerName: "Media",
        field: "files",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => {
          const { images, videos } = getJewelleryMedia(row);
          const thumb = images[0];
          const hasVideos = videos.length > 0;
          const mediaCount = images.length + videos.length;

          if (!thumb && !hasVideos) {
            return (
              <CellWrap align="center">
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: "#f1f5f9",
                    border: "1px dashed #cbd5e1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#94a3b8",
                    flexShrink: 0,
                  }}
                >
                  <ImageOutlinedIcon fontSize="small" />
                </Box>
              </CellWrap>
            );
          }

          return (
            <CellWrap align="center">
              <Tooltip title="Click to preview images & videos">
                <Box
                  onClick={() => handleViewClick(row)}
                  sx={{
                    position: "relative",
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    overflow: "hidden",
                    cursor: "pointer",
                    border: "2px solid #e2e8f0",
                    flexShrink: 0,
                    transition: "all 0.2s ease",
                    bgcolor: thumb ? "transparent" : "#0f172a",
                    "&:hover": {
                      borderColor: "#752ADA",
                      boxShadow: "0 4px 12px rgba(117,42,218,0.2)",
                    },
                  }}
                >
                  {thumb ? (
                    <Box
                      component="img"
                      src={thumb}
                      alt={row.name}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#fff",
                      }}
                    >
                      <PlayCircleOutlineIcon />
                    </Box>
                  )}
                  {mediaCount > 1 && (
                    <Chip
                      label={`+${mediaCount - 1}`}
                      size="small"
                      sx={{
                        position: "absolute",
                        bottom: 2,
                        right: 2,
                        height: 18,
                        fontSize: 10,
                        fontWeight: 700,
                        bgcolor: "rgba(117,42,218,0.9)",
                        color: "#fff",
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                  )}
                  {hasVideos && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 2,
                        left: 2,
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        bgcolor: "rgba(15,23,42,0.75)",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <VideocamOutlinedIcon sx={{ fontSize: 12 }} />
                    </Box>
                  )}
                </Box>
              </Tooltip>
            </CellWrap>
          );
        },
      },
      {
        flex: 0.16,
        minWidth: 140,
        field: "name",
        headerName: "Name",
        align: "left",
        headerAlign: "left",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="left">
            <Tooltip title={row.name}>
              <Typography
                variant="body2"
                fontWeight={600}
                noWrap
                sx={{ color: "#1e293b", width: "100%" }}
              >
                {row.name}
              </Typography>
            </Tooltip>
          </CellWrap>
        ),
      },
      {
        flex: 0.14,
        field: "title",
        minWidth: 110,
        headerName: "Title",
        align: "left",
        headerAlign: "left",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="left">
            <Tooltip title={row.title || "-"}>
              <Typography variant="body2" noWrap sx={{ color: "#1e293b", width: "100%" }}>
                {row.title || "-"}
              </Typography>
            </Tooltip>
          </CellWrap>
        ),
      },
      {
        flex: 0.14,
        minWidth: 110,
        headerName: "Sub-title",
        field: "subtitle",
        align: "left",
        headerAlign: "left",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="left">
            <Tooltip title={row.subtitle || "-"}>
              <Typography variant="body2" noWrap sx={{ color: "#64748b", width: "100%" }}>
                {row.subtitle || "-"}
              </Typography>
            </Tooltip>
          </CellWrap>
        ),
      },
      {
        flex: 0.18,
        minWidth: 140,
        headerName: "Description",
        field: "description",
        align: "left",
        headerAlign: "left",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="left">
            <Tooltip title={row.description || "-"}>
              <Typography variant="body2" noWrap sx={{ color: "#64748b", width: "100%" }}>
                {row.description || "-"}
              </Typography>
            </Tooltip>
          </CellWrap>
        ),
      },
      {
        flex: 0.1,
        minWidth: 90,
        field: "mrp",
        headerName: "MRP",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              {formatAmount(row.mrp)}
            </Typography>
          </CellWrap>
        ),
      },
      {
        flex: 0.1,
        minWidth: 90,
        headerName: "Discount",
        field: "discount",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              {`${Number(row.discount || 0)}%`}
            </Typography>
          </CellWrap>
        ),
      },
      {
        flex: 0.12,
        minWidth: 110,
        headerName: "Final Amount",
        field: "finalAmount",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Typography variant="body2" sx={{ color: "#64748b" }}>
                {formatAmount(row.finalAmount)}
            </Typography>
          </CellWrap>
        ),
      },
      {
        flex: 0.12,
        minWidth: 110,
        headerName: "Status",
        field: "enabled",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => {
          const isLoading = statusLoadingId === row._id;
          if (!canEdit) {
            return (
              <CellWrap align="center">
                <Chip
                  label={row.enabled ? "Enabled" : "Disabled"}
                  size="small"
                  color={row.enabled ? "success" : "default"}
                  variant="outlined"
                />
              </CellWrap>
            );
          }
          return (
            <CellWrap align="center">
              <Button
                size="small"
                variant={row.enabled ? "contained" : "outlined"}
                disabled={isLoading}
                onClick={() => updateStatus(row._id)}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  minWidth: 88,
                  ...(row.enabled
                    ? { bgcolor: "#059669", "&:hover": { bgcolor: "#047857" } }
                    : { color: "#dc2626", borderColor: "#fca5a5" }),
                }}
              >
                {isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : row.enabled ? (
                  "Enabled"
                ) : (
                  "Disabled"
                )}
              </Button>
            </CellWrap>
          );
        },
      },
            // NEW: Details column
      {
        flex: 0.1,
        minWidth: 90,
        headerName: "Details",
        field: "details",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <button
              style={{
                backgroundColor: "#ffc107",
                color: "#000",
                border: "none",
                padding: "5px 10px",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={() => {
                setSelectedJewellery(row);
                setOpenDetailsDialog(true);
              }}
            >
              View
            </button>
          </CellWrap>
        ),
      },

            // NEW: Inventory column
      {
        flex: 0.1,
        minWidth: 110,
        headerName: "Inventory",
        field: "inventory",
        align: "center",
        headerAlign: "center",
        sortable: false,
        renderCell: ({ row }) => (
          <CellWrap align="center">
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setInventoryJewelleryId(row._id);
                setInventoryOpen(true);
              }}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                color: "#752ADA",
                borderColor: "#752ADA",
              }}
            >
              Manage
            </Button>
          </CellWrap>
        ),
      },
      ...(canEdit || canDelete
        ? [
            {
              flex: 0.1,
              minWidth: 100,
              headerName: "Actions",
              field: "actions",
              align: "center",
              headerAlign: "center",
              sortable: false,
              renderCell: ({ row }) => (
                <CellWrap align="center">
                  <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                    {canEdit && (
                      <Tooltip title="Edit jewellery">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditItem(row);
                            setOpen(true);
                          }}
                          sx={{
                            color: "#752ADA",
                            bgcolor: "rgba(117,42,218,0.08)",
                            "&:hover": { bgcolor: "rgba(117,42,218,0.15)" },
                          }}
                        >
                          <FaEdit size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canDelete && (
                      <Tooltip title="Delete jewellery">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setIdToDelete(row._id);
                            setConfirmOpen(true);
                          }}
                          sx={{
                            color: "#dc2626",
                            bgcolor: "rgba(220,38,38,0.08)",
                            "&:hover": { bgcolor: "rgba(220,38,38,0.15)" },
                          }}
                        >
                          <FiTrash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </CellWrap>
              ),
            },
          ]
        : []),
    ],
    [statusLoadingId, updateStatus, handleViewClick, canEdit, canDelete],
  );

  return (
    <Box sx={{ width: "100%", p: { xs: 1, md: 2 } }}>
      <Card
        sx={{
          borderRadius: 3,
          border: "1px solid #e8eaf0",
          boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2.5,
            background: "linear-gradient(135deg, #752ADA 0%, #9b5de5 100%)",
            color: "#fff",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Jewellery
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {total} item{total !== 1 ? "s" : ""} · Click media to preview
              images & videos
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadJewelleryCsv}
              sx={{
                color: "#fff",
                borderColor: "rgba(255,255,255,0.5)",
                textTransform: "none",
                "&:hover": { borderColor: "#fff", bgcolor: "rgba(255,255,255,0.1)" },
              }}
            >
              Download CSV
            </Button>
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditItem(null);
                  setOpen(true);
                }}
                sx={{
                  bgcolor: "#fff",
                  color: "#752ADA",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#f8fafc" },
                }}
              >
                Add Jewellery
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={{ p: 2.5 }}>
          <TextField
            placeholder="Search "
            size="small"
            fullWidth
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 2.5, maxWidth: 420 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />

          <DataGrid
            autoHeight
            rows={rows}
            getRowId={(row) => row._id}
            columns={columns}
            loading={loading}
            paginationMode="server"
            rowCount={total}
            page={page}
            pageSize={pageSize}
            onPageChange={(newPage) => setPage(newPage)}
            onPageSizeChange={(newSize) => {
              setPageSize(newSize);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            rowHeight={64}
            disableSelectionOnClick
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
                minHeight: "48px !important",
                maxHeight: "48px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                color: "#475569",
              },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
                borderColor: "#f1f5f9",
                py: 0,
              },
              "& .MuiDataGrid-row": {
                maxHeight: "none !important",
              },
              "& .MuiDataGrid-row:hover": {
                bgcolor: "rgba(117,42,218,0.04)",
              },
            }}
          />
        </Box>
      </Card>

      <JewelleryDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setEditItem(null);
        }}
        onSubmit={handleSubmit}
        editData={editItem}
        loading={loading}
      />

      <ImagePreviewModal
        open={imageOpen}
        handleClose={handleClose}
        images={previewData.images}
        videos={previewData.videos}
        imageUrl={previewData.images[0]}
        productName={previewData.productName}
        title="Jewellery Media Preview"
      />

            {/* NEW: View Jewellery Details Dialog */}
      <Dialog
        open={openDetailsDialog}
        onClose={() => setOpenDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Jewellery Full Details
          <IconButton onClick={() => setOpenDetailsDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedJewellery ? (
            <>
              <Typography variant="h6">Jewellery Info</Typography>
              <Table size="small" sx={{ mb: 3 }}>
                <TableHead sx={{ backgroundColor: '#b293c7ff' }}> {/* NEW */}
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><b>Product Code</b></TableCell>
                    <TableCell>{selectedJewellery.productCode || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Name</b></TableCell>
                    <TableCell>{selectedJewellery.name || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Title</b></TableCell>
                    <TableCell>{selectedJewellery.title || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Subtitle</b></TableCell>
                    <TableCell>{selectedJewellery.subtitle || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Description</b></TableCell>
                    <TableCell>{selectedJewellery.description || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Category</b></TableCell>
                    <TableCell>{formatCategory(selectedJewellery.category)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Product Type</b></TableCell>
                    <TableCell>{selectedJewellery.productType || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Sub-type</b></TableCell>
                    <TableCell>{selectedJewellery.subType || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Collection</b></TableCell>
                    <TableCell>{selectedJewellery.collection || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Gender</b></TableCell>
                    <TableCell>{selectedJewellery.gender || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Design Style</b></TableCell>
                    <TableCell>{selectedJewellery.designStyle || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Occasion</b></TableCell>
                    <TableCell>{[].concat(selectedJewellery.occasion || []).join(", ") || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Tags</b></TableCell>
                    <TableCell>{[].concat(selectedJewellery.tags || []).join(", ") || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Care Instructions</b></TableCell>
                    <TableCell>{selectedJewellery.careInstructions || "N.A"}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Warranty</b></TableCell>
                    <TableCell>{formatPeriod(selectedJewellery.warrantyPeriod, "Month", "No warranty")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Return and Exchange</b></TableCell>
                    <TableCell>{formatPeriod(selectedJewellery.returnExchangePeriod, "Day", "Not returns")}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Eligibility</b></TableCell>
                    <TableCell>{selectedJewellery.returnExchangeEligibility || "N.A"}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Typography variant="h6">Pricing</Typography>
              <Table size="small">
                <TableHead sx={{ backgroundColor: '#b293c7ff' }}> {/* NEW */}
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell><b>MRP</b></TableCell>
                    <TableCell>{selectedJewellery.mrp == null || selectedJewellery.mrp === "" ? "N.A" : formatAmount(selectedJewellery.mrp)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Discount</b></TableCell>
                    <TableCell>{selectedJewellery.discount == null || selectedJewellery.discount === "" ? "N.A" : `${selectedJewellery.discount}%`}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Include GST</b></TableCell>
                    <TableCell>
                      {selectedJewellery.gstIncluded === true || selectedJewellery.gstIncluded === "true" || selectedJewellery.includeGst === true || selectedJewellery.includeGst === "true" || (selectedJewellery.gstPercentage != null && selectedJewellery.gstPercentage !== "") ? "Yes" : "No"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>GST Percentage</b></TableCell>
                    <TableCell>
                      {selectedJewellery.gstPercentage == null || selectedJewellery.gstPercentage === ""
                        ? "N.A"
                        : `${selectedJewellery.gstPercentage}%`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell><b>Final Amount</b></TableCell>
                    <TableCell>{selectedJewellery.finalAmount == null || selectedJewellery.finalAmount === "" ? "N.A" : formatAmount(selectedJewellery.finalAmount)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </>
          ) : (
            <Typography>Data Not Available</Typography>
          )}
        </DialogContent>
      </Dialog>

        <JewelleryInventory
        open={inventoryOpen}
        onClose={() => setInventoryOpen(false)}
        jewellery={inventoryJewellery}
        stores={stores}
        resetKey={inventoryResetKey}
        loading={loading}
        onAdd={handleAddInventory}
        onEdit={handleEditInventory}
        onToggleActive={handleInventoryToggle}
      />

      <ConfirmationDialog
        title="Delete Jewellery?"
        content="Are you sure you want to delete this jewellery item?"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />
    </Box>
  );
};

export default Jewellery;